import './globalValidations'
import * as Yup from 'yup'
import {
  eulaMandatoryFieldMessage,
  requiredFieldMessage,
  mandatoryUsGovInTitleErrMessage,
  mustHaveMoreWordsForGovCloudMessage,
  websiteValidationCheckMessage,
  doubleQuoteMessage,
  mandatoryFieldMessage,
} from './messagesConstants'
import {
  emptySpaceCharsAtStartAndEndRegex,
  mandatoryStringForUsGovProduct,
} from './constants'
import { ValidateAWSAccountID } from './ValidateAWSAccountID'
import { isEmpty, isNil, isUndefined, get, filter } from 'lodash'
import { noStartingNumber } from './validateFields/ValidateFields'

declare module 'yup' {
  interface ArraySchema<T> {
    uniqueProperty(propertyPath: string, message?: string): ArraySchema<T>
  }
}

declare module 'yup' {
  interface ArraySchema<T> {
    validateDimensionPrice(
      propertyPath: string,
      message?: string
    ): ArraySchema<T>
  }
}

Yup.addMethod(
  Yup.array,
  'validateDimensionPrice',
  function (propertyPath, message) {
    return this.test('validateDimensionPrice', '', function (list: any) {
      const errors: any = []

      list?.forEach((item: any, index: number) => {
        const filteredDimension = list?.filter(
          (i: { subscriptionPrice: any }) =>
            isNil(i?.subscriptionPrice) || i?.subscriptionPrice <= 0
        )

        if (filteredDimension.length === list.length) {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].${propertyPath}`,
              message,
            })
          )
        }
      })

      if (isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }

      return true
    })
  }
)

Yup.addMethod(Yup.array, 'uniqueProperty', function (propertyPath, message) {
  return this.test('unique', '', function (list: any) {
    const errors: any = []

    list?.forEach((item: any, index: number) => {
      const propertyValue = get(item, propertyPath)

      if (
        propertyValue &&
        filter(list, [propertyPath, propertyValue]).length > 1
      ) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${propertyPath}`,
            message,
          })
        )
      }
    })

    if (isEmpty(errors)) {
      throw new Yup.ValidationError(errors)
    }

    return true
  })
})

export const dimensionValidations = (value: string[]) => {
  return Yup.array()
    .of(
      Yup.object().shape({
        label: Yup.string()
          .trim(requiredFieldMessage)
          .required(requiredFieldMessage)
          .max(80, 'Max 80 characters are allowed')
          .test(
            'no-starting-number',
            'Display name cannot start with number',
            noStartingNumber
          ),
        labelDescription: Yup.string()
          .trim(requiredFieldMessage)
          .required(requiredFieldMessage)
          .max(1000, 'Max 1000 characters are allowed'),
        price1m: value?.includes('1m')
          ? Yup.number()
              .typeError('1 month price must be a valid number')
              .min(0, 'Not negative number')
              .checkThreeDecimalpointValidation({
                message: 'Only 3 decimal places allowed.',
              })
              .required('1 month price is required')
          : Yup.number()
              .transform(value => (isNaN(value) ? undefined : value))
              .nullable(),

        price12m:
          value && value.includes('12m')
            ? Yup.number()
                .typeError('12 months price must be a valid number')
                .min(0, 'Not negative number')
                .checkThreeDecimalpointValidation({
                  message: 'Only 3 decimal places allowed.',
                })
                .required('12 months price is required')
            : Yup.number()
                .transform(value => (isNaN(value) ? undefined : value))
                .nullable(),

        price24m: value?.includes('24m')
          ? Yup.number()
              .typeError('24 months price must be a valid number')
              .min(0, 'Not negative number')
              .required('24 months price is required')
              .checkThreeDecimalpointValidation({
                message: 'Only 3 decimal places allowed.',
              })
          : Yup.number()
              .transform(value => (isNaN(value) ? undefined : value))
              .nullable(),

        price36m: value?.includes('36m')
          ? Yup.number()
              .typeError('36 months price must be a valid number')
              .min(0, 'Not negative number')
              .checkThreeDecimalpointValidation({
                message: 'Only 3 decimal places allowed.',
              })
              .required('36 months price is required')
          : Yup.number()
              .transform(value => (isNaN(value) ? undefined : value))
              .nullable(),
      })
    )
    .uniqueProperty('label', 'Display name must be unique')
}

export const productDimesnsionsHelperSchema = Yup.object({
  dimensionCategoryName: Yup.string().required('1 option is Required'),
  contractDuration: Yup.array()
    .min(1, 'Please select atleast 1 item')
    .required(),
  additionalDimensions: Yup.array().of(
    Yup.object().shape({
      name: Yup.string(),
      label: Yup.string()
        .trim(requiredFieldMessage)
        .required(requiredFieldMessage)
        .max(80, 'Max 80 characters are allowed')
        .test(
          'no-starting-number',
          'Display name cannot start with number',
          noStartingNumber
        ),
      additionalUsageDescription: Yup.string()
        .trim(requiredFieldMessage)
        .required(requiredFieldMessage)
        .max(90, 'Max 90 characters are allowed')
        .test(
          'no-starting-number',
          'Display name cannot start with number',
          noStartingNumber
        )
        .nullable(),

      additionalUsagePrice: Yup.number()
        .typeError('Price must be a valid number')
        .min(0, 'Not negative number')
        .checkThreeDecimalpointValidation({
          message: 'Only 3 decimal places allowed.',
        })
        .required('Price is required'),
    })
  ),
  dimensions: Yup.array().when('contractDuration', value => {
    return dimensionValidations(value)
  }),

  additionalDimensionsRequired: Yup.array()
    .of(
      Yup.object().shape({
        labelDescription: Yup.string()
          .trim(requiredFieldMessage)
          .required(requiredFieldMessage)
          .max(90, 'Max 90 characters are allowed')
          .nullable(),

        subscriptionPrice: Yup.number()
          .typeError('Price must be a valid number')
          .min(0, 'Not negative number')
          .checkThreeDecimalpointValidation({
            message: 'Only 3 decimal places allowed.',
          })
          .required('Price is required'),
      })
    )
    .validateDimensionPrice(
      'subscriptionPrice',
      'Enter a price greater than $0.'
    ),
})

export const generalInformationHelperSchema = Yup.object({
  supportResources: Yup.string()
    .trim(requiredFieldMessage)
    .required(requiredFieldMessage)
    .max(2000, 'Max 2000 characters are allowed'),
  resourceUrls: Yup.array().of(
    Yup.object().shape({
      Text: Yup.string()
        .trim(requiredFieldMessage)
        .test(
          'resourceUrlPresence',
          'Resource name is required when URL is provided.',
          (value, context) => {
            const { Url } = context.parent
            if (isEmpty(Url) && isEmpty(value)) {
              return true
            }
            return !isEmpty(Url) ? !isEmpty(value) : true
          }
        )
        .max(500, 'Max 500 characters are allowed')
        .nullable()
        .optional(),
      Url: Yup.string()
        .trim(requiredFieldMessage)
        .test(
          'resourceNamePresence',
          'Resource URL is required when name is provided.',
          (value, context) => {
            const { Text } = context.parent
            if (isEmpty(Text) && isEmpty(value)) {
              return true
            }
            return !isEmpty(Text) ? !isEmpty(value) : true
          }
        )
        .url(websiteValidationCheckMessage)
        .nullable()
        .optional(),
    })
  ),
  sku: Yup.string()
    .trim(requiredFieldMessage)
    .max(100, 'Max 100 characters are allowed')
    .nullable()
    .optional(),

  shortProductDescription: Yup.string()
    .trim(requiredFieldMessage)
    .required(requiredFieldMessage)
    .max(1000, 'Description must be 1,000 characters or less'),

  productDescription: Yup.string()
    .trim(requiredFieldMessage)
    .required(requiredFieldMessage)
    .max(5000, 'Max 5000 characters are allowed'),

  productVideoLink: Yup.string()
    .trim(requiredFieldMessage)
    .url(`${websiteValidationCheckMessage}`)
    .nullable()
    .optional(),

  apiEndpointUrl: Yup.string()
    .trim()
    .transform(value =>
      value && !/^https?:\/\//i.test(value) ? `https://${value}` : value
    )
    .url(websiteValidationCheckMessage)
    .required(requiredFieldMessage)
    .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`),

  apiUsageInstructions: Yup.string()
    .required(requiredFieldMessage)
    .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
    .max(4000, 'Description must be 4,000 characters or less'),
  authorizationMethod: Yup.array()
    .required(requiredFieldMessage)
    .min(1, 'At least 1 category is required'),
  apiIntegrationProtocols: Yup.array().of(Yup.string()).nullable().optional(),
  a2aUsageInstructions: Yup.string().when('apiIntegrationProtocols', {
    is: (protocols: string[]) =>
      Array.isArray(protocols) && protocols.includes('A2A'),
    then: schema =>
      schema
        .required('A2A usage instructions are required')
        .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
        .max(4000, 'Max 4000 characters are allowed'),
    otherwise: schema => schema.nullable().optional(),
  }),
  mcpUsageInstructions: Yup.string().when('apiIntegrationProtocols', {
    is: (protocols: string[]) =>
      Array.isArray(protocols) && protocols.includes('MCP'),
    then: schema =>
      schema
        .required('MCP usage instructions are required')
        .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
        .max(4000, 'Max 4000 characters are allowed'),
    otherwise: schema => schema.nullable().optional(),
  }),

  highlights: Yup.array().of(
    Yup.string()
      .trim(requiredFieldMessage)
      .required(requiredFieldMessage)
      .max(500, 'Each highlight must be 500 characters or less')
  ),
  eula: Yup.string().required('1 option is Required'),
  eulaUrl: Yup.string().when('eula', {
    is: 'CUSTOM',
    then: Yup.string().required(eulaMandatoryFieldMessage),
    otherwise: Yup.string(),
  }),
  multipleDimensionsAllowed: Yup.boolean()
    .nullable(true)
    .test(
      'multipleDimensionsAllowed',
      '1 option is Required',
      (value, context) => {
        return (
          !isNil(value) && !isUndefined(value) && [true, false].includes(value)
        )
      }
    ),
  productLogoUrl: Yup.string().required('Please provide a logo'),
  allowedAccounts: Yup.string()
    .test('valid-account-ids', '12 digits allowed', ValidateAWSAccountID)
    .nullable()
    .optional(),
  refundPolicy: Yup.string()
    .trim(requiredFieldMessage)
    .required(requiredFieldMessage)
    .max(500, 'Max 500 characters are allowed'),
  searchKeywords: Yup.string()
    .required('Search Keywords are required')
    .test(
      'max-keywords',
      'You can only add up to 15 keywords',
      function (value) {
        const keywordsArray = value
          ? value.split(',').map(keyword => keyword.trim())
          : []
        return keywordsArray.length <= 15
      }
    )
    .test(
      'max-keyword-length',
      'Each keyword must be less than 50 characters',
      function (value) {
        const keywordsArray = value
          ? value.split(',').map(keyword => keyword.trim())
          : []
        return keywordsArray.every(keyword => keyword.length <= 50)
      }
    )
    .test(
      'max-total-length',
      'Max 250 total combined character are allowed',
      function (value) {
        const keywordsArray = value
          ? value.split(',').map(keyword => keyword.trim())
          : []
        const totalLength = keywordsArray.reduce(
          (sum, keyword) => sum + keyword.length,
          0
        )
        return totalLength <= 250
      }
    ),
  productCategories: Yup.array()
    .min(1, 'At least 1 category is required')
    .max(3, 'Maximum 3 categories allowed'),
  countryAvailability: Yup.string().trim().strict(true).required(),
  countriesSelected: Yup.array().when('countryAvailability', {
    is: (value: string) =>
      value === 'excludedCountries' || value === 'includedCountries',
    then: Yup.array()
      .min(
        1,
        'Countries is required when excluded countries or included countries are selected'
      )
      .required(
        'Countries is required when excluded countries or included countries are selected'
      ),
    otherwise: Yup.array(),
  }),
  title: Yup.string()
    .trim(requiredFieldMessage)
    .required(requiredFieldMessage)
    .doubleQuoteValidation({ message: doubleQuoteMessage })
    .max(72, 'Max 72 characters are allowed')
    .test('validateGov', mandatoryUsGovInTitleErrMessage, (value, context) => {
      if (!value) {
        return true
      }
      return context.parent.isUsGovProduct
        ? value.includes(mandatoryStringForUsGovProduct)
        : true
    })
    .test(
      'shouldIncludeOtherText',
      mustHaveMoreWordsForGovCloudMessage,
      (value, context) => {
        if (!value) {
          return true
        }
        return context.parent.isUsGovProduct
          ? value.includes(mandatoryStringForUsGovProduct) &&
              !(value.length <= mandatoryStringForUsGovProduct.length)
          : true
      }
    ),
})

export const termsAndConditionsHelperSchema = Yup.object().shape({
  generalInformation: Yup.array().of(
    Yup.object().shape({
      type: Yup.string()
        .oneOf(['checkbox', 'radio'])
        .required('Type is required'),
      label: Yup.string(),
      value: Yup.boolean().when('type', {
        is: 'checkbox',
        then: Yup.boolean().oneOf(
          [true],
          'You must check all boxes before continuing.'
        ),
        otherwise: Yup.boolean(), // Adjust as needed for other types
      }),
      subTermsWhenTrue: Yup.object().when(['type', 'value'], {
        is: (type: string, value: boolean) =>
          type === 'radio' && value === true,
        then: Yup.object({
          label: Yup.string(),
          value: Yup.boolean().oneOf(
            [true],
            'You must check all boxes before continuing.'
          ),
          type: Yup.string().oneOf(['checkbox'], 'Invalid type'),
        }),
        otherwise: Yup.object().notRequired(),
      }),
    })
  ),
  productDimensions: Yup.array().of(
    Yup.object().shape({
      type: Yup.string()
        .oneOf(['checkbox', 'radio'])
        .required('Type is required'),
      label: Yup.string(),
      value: Yup.boolean().when('type', {
        is: 'checkbox',
        then: Yup.boolean().oneOf(
          [true],
          'You must check all boxes before continuing.'
        ),
        otherwise: Yup.boolean(), // Adjust as needed for other types
      }),
      subTermsWhenTrue: Yup.object().when(['type', 'value'], {
        is: (type: string, value: boolean) =>
          type === 'radio' && value === true,
        then: Yup.object({
          label: Yup.string(),
          value: Yup.boolean().oneOf(
            [true],
            'You must check all boxes before continuing.'
          ),
          type: Yup.string().oneOf(['checkbox'], 'Invalid type'),
        }),
        otherwise: Yup.object().notRequired(),
      }),
    })
  ),
  infoCollectionFormMetaData: Yup.array()
    .of(
      Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.bool().oneOf(
          [true],
          'You must check all boxes before continuing.'
        ),
        type: Yup.string().oneOf(['checkbox']).required(),
      })
    )
    .required()
    .min(5, 'You must check all boxes before continuing.'),
  notificationRecipients: Yup.array()
    .of(
      Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.bool().oneOf(
          [true],
          'You must check all boxes before continuing.'
        ),
        type: Yup.string().oneOf(['checkbox']).required(),
      })
    )
    .required()
    .min(3, 'You must check all boxes before continuing.'),
})
